import { useRealtimeRefresh } from "@/hooks/useRealtimeRefresh";
import { supabase } from "@/lib/supabase";
import type { Json } from "@/types/database.types";
import type { AuditEntry, AuditOperation } from "@/utils/auditRollup";
import { useCallback, useEffect, useState } from "react";

/**
 * The audit_log.changes column is opaque JSON; reshape it to the
 * field-change map the UI expects, ignoring non-object payloads.
 */
function toChanges(value: Json | null): AuditEntry["changes"] {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const changes: AuditEntry["changes"] = {};
  for (const [field, change] of Object.entries(value)) {
    if (change && typeof change === "object" && !Array.isArray(change)) {
      changes[field] = { old: change.old, new: change.new };
    }
  }
  return changes;
}

export interface UseAuditLogDataResult {
  entries: AuditEntry[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

/**
 * Loads raw audit_log rows (newest first) and keeps them live via realtime.
 * The page reshapes them one-to-one for display via toRolledUpEntries.
 */
export function useAuditLogData(limit = 500): UseAuditLogDataResult {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    const { data, error: fetchError } = await supabase
      .from("audit_log")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(limit);

    if (fetchError) {
      setError(fetchError.message);
      setLoading(false);
      return;
    }

    setEntries(
      (data ?? []).map((row) => ({
        id: row.id,
        table_name: row.table_name,
        record_id: row.record_id,
        operation: row.operation as AuditOperation,
        actor_id: row.actor_id,
        actor_email: row.actor_email,
        actor_name: row.actor_name,
        record_label: row.record_label,
        office_id: row.office_id,
        changes: toChanges(row.changes),
        created_at: row.created_at,
      })),
    );
    setLoading(false);
  }, [limit]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // audit_log is append-only; a full refetch on any change appends new rows at top.
  useRealtimeRefresh(["audit_log"], refresh);

  return { entries, loading, error, refresh };
}
